(function () {
  'use strict';

  if (customElements.get('scroll-media-effects')) return;

  var MOBILE_QUERY = '(max-width: 989px)';
  var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  class ScrollMediaEffects extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedController) return;
      this.isConnectedController = true;

      var computedZoomScale =
        parseFloat(window.getComputedStyle(this).getPropertyValue('--scroll-media-zoom-scale')) || 1.2;

      this.desktopSettings = {
        effect: this.dataset.scrollEffectDesktop || this.dataset.scrollEffect || 'none',
        parallaxDirection:
          (this.dataset.parallaxDirectionDesktop || this.dataset.parallaxDirection) === 'horizontal'
            ? 'horizontal'
            : 'vertical',
        parallaxSpeed: this.parseParallaxSpeed(
          this.dataset.parallaxSpeedDesktop || this.dataset.parallaxSpeed
        ),
        zoomScale: this.parseZoomScale(this.dataset.zoomScaleDesktop, computedZoomScale),
        hasCustomParallaxSpeed:
          this.dataset.parallaxSpeedDesktop !== undefined || this.dataset.parallaxSpeed !== undefined
      };
      this.mobileSettings = {
        effect: this.dataset.scrollEffectMobile || 'inherit',
        parallaxDirection:
          this.dataset.parallaxDirectionMobile === 'horizontal' ? 'horizontal' : 'vertical',
        parallaxSpeed: this.parseParallaxSpeed(this.dataset.parallaxSpeedMobile),
        zoomScale: this.parseZoomScale(this.dataset.zoomScaleMobile, computedZoomScale),
        hasCustomParallaxSpeed: this.dataset.parallaxSpeedMobile !== undefined
      };
      this.mediaVisuals = Array.from(this.querySelectorAll('[data-scroll-media-visual]'));
      this.videos = Array.from(this.querySelectorAll('theme-video video'));
      this.mobileMedia = window.matchMedia(MOBILE_QUERY);
      this.reducedMotionMedia = window.matchMedia(REDUCED_MOTION_QUERY);
      this.frame = 0;

      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleVideoPlay = this.handleVideoPlay.bind(this);
      this.handleKeyboardNavigationChange = this.handleKeyboardNavigationChange.bind(this);
      this.update = this.update.bind(this);

      this.mobileMedia.addEventListener('change', this.handleViewportChange);
      this.reducedMotionMedia.addEventListener('change', this.handleViewportChange);
      document.addEventListener('theme:keyboard-navigation-change', this.handleKeyboardNavigationChange);
      this.videos.forEach((video) => video.addEventListener('play', this.handleVideoPlay));
      this.handleViewportChange();
    }

    disconnectedCallback() {
      this.removeScrollListeners();
      this.mobileMedia?.removeEventListener('change', this.handleViewportChange);
      this.reducedMotionMedia?.removeEventListener('change', this.handleViewportChange);
      document.removeEventListener('theme:keyboard-navigation-change', this.handleKeyboardNavigationChange);
      this.videos?.forEach((video) => video.removeEventListener('play', this.handleVideoPlay));
      this.isConnectedController = false;
    }

    handleVideoPlay(event) {
      if (this.reducedMotionMedia.matches) event.currentTarget.pause();
    }

    handleKeyboardNavigationChange() {
      this.handleViewportChange();
    }

    handleViewportChange() {
      this.applyViewportSettings();

      var motionEnabled =
        !this.reducedMotionMedia.matches &&
        (this.effect === 'parallax' || this.effect === 'zoom_in' || this.effect === 'zoom_out') &&
        this.mediaVisuals.length > 0;

      this.removeScrollListeners();

      if (!motionEnabled) {
        this.style.setProperty('--scroll-media-translate-x', '0%');
        this.style.setProperty('--scroll-media-translate-y', '0%');
        this.style.setProperty('--scroll-media-scale', '1');
        if (this.reducedMotionMedia.matches) {
          this.videos.forEach((video) => {
            if (!video.paused) video.pause();
          });
        }
        return;
      }

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleScroll, { passive: true });
      this.handleScroll();
    }

    applyViewportSettings() {
      var useMobileSettings = this.mobileMedia.matches && this.mobileSettings.effect !== 'inherit';
      var activeSettings = useMobileSettings ? this.mobileSettings : this.desktopSettings;

      this.effect =
        document.body.classList.contains('theme-keyboard-navigation') && activeSettings.effect === 'sticky'
          ? 'none'
          : activeSettings.effect;
      this.parallaxDirection = activeSettings.parallaxDirection;
      this.parallaxSpeed = activeSettings.parallaxSpeed;
      this.zoomScale = activeSettings.zoomScale;
      this.hasCustomParallaxSpeed = activeSettings.hasCustomParallaxSpeed;

      // Keep the existing CSS contract as the active value while preserving
      // the desktop/mobile source settings in their dedicated data attributes.
      this.dataset.scrollEffect = this.effect;
    }

    parseParallaxSpeed(value) {
      return Math.min(Math.max(parseFloat(value) || 12, 1), 30);
    }

    parseZoomScale(value, fallback) {
      return Math.max(parseFloat(value) || fallback || 1.2, 1);
    }

    isPageScrollLocked() {
      return document.documentElement.classList.contains('is-scroll-locked');
    }

    handleScroll() {
      if (this.isPageScrollLocked()) return;
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(this.update);
    }

    update() {
      this.frame = 0;
      if (this.isPageScrollLocked()) return;

      var rect = this.getBoundingClientRect();
      var travel = Math.max(window.innerHeight + rect.height, 1);
      var progress = Math.min(Math.max((window.innerHeight - rect.top) / travel, 0), 1);
      if (this.dataset.scrollProgress === 'runway') {
        var sectionRunway = this.offsetHeight - window.innerHeight;
        travel = sectionRunway > 1 ? sectionRunway : window.innerHeight;
        progress = Math.min(Math.max(-rect.top / travel, 0), 1);
      }
      var translateX = 0;
      var translateY = 0;
      var scale = 1;
      var zoomDelta = this.zoomScale - 1;

      if (this.effect === 'parallax') {
        var parallaxOffset = this.parallaxSpeed / 2 - progress * this.parallaxSpeed;
        if (this.parallaxDirection === 'horizontal') {
          translateX = parallaxOffset;
        } else {
          translateY = parallaxOffset;
        }
        scale = this.hasCustomParallaxSpeed ? 1 + this.parallaxSpeed / 100 : 1.08;
      } else if (this.effect === 'zoom_in') {
        scale = 1 + progress * zoomDelta;
      } else if (this.effect === 'zoom_out') {
        scale = this.zoomScale - progress * zoomDelta;
      }

      this.style.setProperty('--scroll-media-translate-x', translateX.toFixed(3) + '%');
      this.style.setProperty('--scroll-media-translate-y', translateY.toFixed(3) + '%');
      this.style.setProperty('--scroll-media-scale', scale.toFixed(4));
    }

    removeScrollListeners() {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleScroll);

      if (this.frame) {
        window.cancelAnimationFrame(this.frame);
        this.frame = 0;
      }
    }
  }

  customElements.define('scroll-media-effects', ScrollMediaEffects);
})();
